import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { getErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Lock, Star, Sparkles, BookOpen, Printer, Heart, MessageCircle } from "lucide-react";
import Panda from "../components/Panda";
import { CurveBottom, Cloud } from "../components/Decor";
import { useProgress } from "../context/ProgressContext";
import { STICKERS, STICKER_STEP, unlockedCount, nextToEarn, ownsSticker, ownedCount, giftsById } from "../data/stickers";

const THANKS = ["Thanks!", "You're the best!", "Best sibling ever!"];

// Recipient side: say thanks for a gifted sticker (one per gift, editable)
const ThanksBox = ({ child, sticker, gift, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(gift.thanks?.text || "");
  const [busy, setBusy] = useState(false);
  const save = async (t) => {
    const body = (t ?? text).trim();
    if (!body) return;
    setBusy(true);
    try {
      const r = await api.put(`/children/${child.id}/gifts/${sticker.id}/thanks`, { text: body });
      onSaved(r.data);
      setOpen(false);
      toast.success(`${child.panda.name} said thanks to ${gift.from_name}!`, { description: `"${body}" now shows in both sticker books.` });
    } catch (err) { toast.error(getErrorMessage(err, "Could not send thanks")); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2" data-testid={`thanks-box-${sticker.id}`}>
      {gift.thanks && !open && <p className="text-xs text-[#0d4d33] bg-[#e8f8ef] rounded-xl px-2.5 py-1.5 inline-flex items-center gap-1" data-testid={`thanks-text-${sticker.id}`}><Heart size={11} className="text-[#e5356f]" fill="#e5356f" /> {child.panda.name} said: “{gift.thanks.text}”</p>}
      {open ? (
        <div className="mt-1.5 space-y-1.5">
          <div className="flex flex-wrap gap-1">{THANKS.map((t) => <button key={t} type="button" onClick={() => save(t)} disabled={busy} className="text-[11px] font-bold rounded-full px-2.5 py-1 bg-[#f3e8ff] text-[#7d5fb2] hover:bg-[#7d5fb2] hover:text-white" data-testid={`thanks-quick-${sticker.id}`}>{t}</button>)}</div>
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={60} placeholder="Or write your own..." className="w-full min-w-0 h-8 rounded-full px-3 border-2 border-[#c9ecf5] text-xs focus:outline-none focus:border-[#7d5fb2]" data-testid={`thanks-input-${sticker.id}`} onKeyDown={(e) => { if (e.key === "Enter") save(); }} />
          <div className="flex gap-1.5">
            <button type="button" onClick={() => save()} disabled={busy || !text.trim()} className="flex-1 h-7 text-xs font-bold text-white bg-[#7d5fb2] rounded-full px-3 disabled:opacity-50" data-testid={`thanks-save-${sticker.id}`}>Send</button>
            <button type="button" onClick={() => setOpen(false)} className="h-7 text-xs text-gray-400 px-2" data-testid={`thanks-cancel-${sticker.id}`}>Cancel</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)} className="mt-1 text-xs font-bold text-[#7d5fb2] hover:underline inline-flex items-center gap-1" data-testid={`thanks-button-${sticker.id}`}><MessageCircle size={12} /> {gift.thanks ? "Edit thanks" : `Say thanks to ${gift.from_name}`}</button>
      )}
    </div>
  );
};

const StickerCard = ({ sticker, unlocked, isNew, gift, given, child, canThank, onThanks }) => (
  <div className={`relative rounded-3xl p-3 transition-transform ${unlocked ? "bg-white shadow-md hover:-translate-y-1 hover:rotate-1 z-10 focus-within:z-20" : "bg-white/60 border-2 border-dashed border-[#c9ecf5]"}`} data-testid={`sticker-${sticker.id}`} data-unlocked={unlocked ? "true" : "false"} data-gift={gift ? "true" : "false"}>
    {gift && <span className="absolute top-3 left-0 z-10 bg-[#7d5fb2] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-r-full shadow" data-testid={`sticker-gift-${sticker.id}`}>Gift from {gift.from_name}</span>}
    {given?.length > 0 && !gift && <span className="absolute top-3 left-0 z-10 bg-[#63c79a] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-r-full shadow" data-testid={`sticker-given-${sticker.id}`}>Gifted to {given.map((g) => g.to_name).join(", ")}</span>}
    <div className="aspect-square rounded-2xl overflow-hidden bg-[#f7fdfe] flex items-center justify-center">
      <img src={`/stickers/${sticker.file}`} alt={unlocked ? sticker.name : "Locked sticker"} className={`w-full h-full object-cover ${unlocked ? "" : "grayscale opacity-30 blur-[2px]"}`} loading="lazy" />
    </div>
    {!unlocked && (
      <div className="absolute inset-0 flex flex-col items-center justify-center text-[#0c4a6e]">
        <span className="w-12 h-12 rounded-full bg-white shadow flex items-center justify-center"><Lock size={20} /></span>
        <span className="mt-2 text-xs font-bold bg-white rounded-full px-3 py-1 shadow" data-testid={`sticker-threshold-${sticker.id}`}>Unlocks at {sticker.threshold} bamboo</span>
      </div>
    )}
    {isNew && <span className="absolute -top-2 -right-2 bg-[#e5356f] text-white text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-1 shadow heart-pop" data-testid="sticker-new-badge">New!</span>}
    <div className="mt-3 px-1">
      <p className={`font-bold ${unlocked ? "text-[#0c4a6e]" : "text-gray-400"}`}>{unlocked ? sticker.name : "???"}</p>
      <p className="text-xs text-gray-500 mt-0.5 leading-snug">{unlocked ? sticker.blurb : `Earn ${sticker.threshold} bamboo to reveal this sticker.`}</p>
      {gift && canThank && <ThanksBox child={child} sticker={sticker} gift={gift} onSaved={onThanks} />}
      {gift && !canThank && gift.thanks && <p className="mt-2 text-xs text-[#0d4d33] bg-[#e8f8ef] rounded-xl px-2.5 py-1.5" data-testid={`thanks-text-${sticker.id}`}>{child.panda.name} said: “{gift.thanks.text}”</p>}
      {given?.filter((g) => g.thanks).map((g) => <p key={g.to_child_id} className="mt-2 text-xs text-[#0d4d33] bg-[#e8f8ef] rounded-xl px-2.5 py-1.5 inline-flex items-center gap-1" data-testid={`thanks-received-${sticker.id}`}><Heart size={11} className="text-[#e5356f]" fill="#e5356f" /> {g.to_name} said thanks for your gift: “{g.thanks.text}”</p>)}
    </div>
  </div>
);

const StickerBook = () => {
  const { activeChild, bamboo, panda, refreshChildren } = useProgress();
  const { user } = useAuth();
  const canThank = !!user && !!activeChild?.ownerId && !activeChild?.readOnly;
  const givenBySticker = (activeChild?.giftsGiven || []).reduce((a, g) => ({ ...a, [g.sticker_id]: [...(a[g.sticker_id] || []), g] }), {});

  // Toast the giver once per new thank-you they haven't seen yet
  useEffect(() => {
    if (!activeChild) return;
    const key = "tycc_thanks_seen_v1";
    const seen = JSON.parse(localStorage.getItem(key) || "[]");
    const fresh = (activeChild.giftsGiven || []).filter((g) => g.thanks && !seen.includes(`${g.sticker_id}:${g.to_child_id}:${g.thanks.at}`));
    if (!fresh.length) return;
    // Defer past mount so the Toaster is subscribed; only mark seen once actually shown
    const t = setTimeout(() => {
      fresh.forEach((g) => toast.success(`${g.to_name} said thanks for your gift!`, { description: `"${g.thanks.text}"`, duration: 6000 }));
      localStorage.setItem(key, JSON.stringify([...seen, ...fresh.map((g) => `${g.sticker_id}:${g.to_child_id}:${g.thanks.at}`)]));
    }, 300);
    return () => clearTimeout(t);
  }, [activeChild]);
  const earned = unlockedCount(bamboo);
  const count = ownedCount(activeChild);
  const gifts = giftsById(activeChild);
  const next = nextToEarn(activeChild);
  const toNext = next ? Math.max(0, next.threshold - bamboo) : 0;
  const pct = next ? Math.max(0, Math.min(100, Math.round(((bamboo - (next.threshold - STICKER_STEP)) / STICKER_STEP) * 100))) : 100;
  const latestId = earned > 0 ? STICKERS[earned - 1].id : null;

  return (
    <div data-testid="sticker-book">
      <section className="relative bg-[#f7931e] overflow-hidden">
        <Cloud className="absolute right-[10%] top-6 hidden md:block cloud-drift" size={160} />
        <div className="relative max-w-6xl mx-auto px-6 pt-12 pb-24 flex flex-col md:flex-row md:items-center gap-8">
          <div className="flex-1">
            <p className="text-[#fff3d6] font-bold uppercase tracking-widest text-sm inline-flex items-center gap-2"><BookOpen size={16} /> Rewards</p>
            <h1 className="text-white font-bold text-4xl sm:text-5xl mt-2" style={{ fontFamily: "Literata, serif" }}>{panda?.name ? `${panda.name}'s sticker book` : "Sticker book"}</h1>
            <p className="text-white/90 text-lg mt-3 max-w-xl">A new panda sticker every {STICKER_STEP} bamboo. Lesson steps earn 5, quizzes earn 10 - keep going!</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {activeChild && <span className="bg-white/20 text-white rounded-full px-4 py-2 font-bold text-sm inline-flex items-center gap-1" data-testid="sticker-count"><Sparkles size={14} /> {count} of {STICKERS.length} stickers</span>}
              {activeChild && <span className="bg-white text-[#7a3d00] rounded-full px-4 py-2 font-bold text-sm inline-flex items-center gap-1" data-testid="sticker-bamboo"><Star size={14} fill="#f3c932" className="text-[#f3c932]" /> {bamboo} bamboo</span>}
            </div>
            {!activeChild ? null : next ? (
              <div className="mt-5 max-w-md" data-testid="sticker-next">
                <div className="flex justify-between text-sm text-white font-bold"><span>Next: a mystery sticker</span><span>{toNext} bamboo to go</span></div>
                <div className="mt-1.5 h-3 rounded-full bg-white/30 overflow-hidden"><div className="h-full bg-white rounded-full transition-[width] duration-700" style={{ width: `${Math.max(4, pct)}%` }} /></div>
              </div>
            ) : (
              <p className="mt-5 text-white font-bold" data-testid="sticker-complete">Every sticker collected - you're a Credit Grad!</p>
            )}
          </div>
          {panda?.name && <Panda size={170} accessory={panda.accessory} scarf={panda.scarf} item="coin" animate />}
        </div>
        <CurveBottom color="#f7fdfe" />
      </section>

      <section className="bg-[#f7fdfe] pb-20">
        <div className="max-w-6xl mx-auto px-6">
          {!activeChild ? (
            <div className="bg-white rounded-3xl shadow-md p-12 text-center">
              <Panda size={120} accessory="hat" scarf="#f7931e" animate className="mx-auto" />
              <h2 className="text-2xl font-bold text-[#0c4a6e] mt-4" style={{ fontFamily: "Literata, serif" }}>Create a panda to start collecting</h2>
              <Link to="/panda" className="mt-6 inline-block btn-pill-lg bg-[#e5356f] hover:bg-[#c92a5e] text-white">Create a panda</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5" data-testid="sticker-grid">
              {STICKERS.map((s) => <StickerCard key={s.id} sticker={s} unlocked={ownsSticker(activeChild, s)} gift={gifts[s.id]} given={givenBySticker[s.id]} child={activeChild} canThank={canThank} onThanks={() => refreshChildren()} isNew={s.id === latestId && bamboo - s.threshold < STICKER_STEP} />)}
            </div>
          )}
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/lessons" className="btn-pill-lg bg-[#63c79a] hover:bg-[#4fb98a] text-white inline-flex items-center gap-2" data-testid="sticker-earn-more"><Star size={18} /> Earn more bamboo</Link>
            {activeChild && <Link to="/stickers/print" className="btn-pill-lg bg-white border-2 border-[#f7931e] text-[#7a3d00] hover:bg-[#fff3d6] inline-flex items-center gap-2" data-testid="sticker-print-link"><Printer size={18} /> Print sticker sheet</Link>}
          </div>
        </div>
      </section>
    </div>
  );
};

export default StickerBook;
